// src/components/settings/PublicHolidayManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { settingsService } from '../../services/settingsService';
import { premisesService } from '../../services/premisesService';
import SearchableSelect from '../common/SearchableSelect';
import LoadingSpinner from '../common/LoadingSpinner';
import Toast from '../common/Toast';
import Pagination from '../common/Pagination';
import Modal from '../common/Modal';

const PublicHolidayManagement = () => {
    const { user } = useAuth();
    const [holidays, setHolidays] = useState([]);
    const [countries, setCountries] = useState([]);
    const [premisesOptions, setPremisesOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCountry, setFilterCountry] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingHoliday, setEditingHoliday] = useState(null);
    const [formData, setFormData] = useState({ name: '', holiday_date: '', country_id: '', premises_id: 0, status: 1 });
    const [formErrors, setFormErrors] = useState({});
    const [saveLoading, setSaveLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 20,
        total: 0,
        total_pages: 1
    });

    const isCoreAdmin = user?.user_type === 'core_admin';

    const loadCountries = useCallback(async () => {
        try {
            let allCountries = [];
            let page = 1;
            let totalPages = 1;

            do {
                const response = await settingsService.getCountries({ page, limit: 100 });
                allCountries = allCountries.concat(response.countries || []);
                totalPages = response.pagination?.total_pages || 1;
                page += 1;
            } while (page <= totalPages);

            setCountries(allCountries);
        } catch (error) {
            console.error('Error loading countries:', error);
        }
    }, []);

    const loadPremisesOptions = useCallback(async () => {
        try {
            let allPremises = [];

            if (isCoreAdmin) {
                let page = 1;
                let totalPages = 1;
                do {
                    const response = await premisesService.getAllPremises({ page, limit: 100, search: '', organization_id: '', status: '' });
                    const currentPageRows = response?.premises || [];
                    allPremises = allPremises.concat(currentPageRows);
                    totalPages = response?.pagination?.total_pages || 1;
                    page += 1;
                } while (page <= totalPages);
            } else {
                let page = 1;
                let totalPages = 1;
                do {
                    const response = await premisesService.getPremises({ page, limit: 100 });
                    const currentPageRows = response?.premises || [];
                    allPremises = allPremises.concat(currentPageRows);
                    totalPages = response?.pagination?.total_pages || 1;
                    page += 1;
                } while (page <= totalPages);
            }

            setPremisesOptions(allPremises);
        } catch (error) {
            console.error('Error loading premises for holidays:', error);
            setPremisesOptions([]);
        }
    }, [isCoreAdmin]);

    const loadHolidays = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.current_page,
                limit: pagination.per_page,
                search: searchTerm,
                country_id: filterCountry
            };
            const response = await settingsService.getPublicHolidays(params);
            setHolidays(response.holidays || []);
            if (response.pagination) {
                setPagination(response.pagination);
            }
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [pagination.current_page, pagination.per_page, searchTerm, filterCountry]);

    useEffect(() => {
        loadCountries();
    }, [loadCountries]);

    useEffect(() => {
        loadPremisesOptions();
    }, [loadPremisesOptions]);

    useEffect(() => {
        loadHolidays();
    }, [loadHolidays]);

    useEffect(() => {
        setPagination(prev => ({ ...prev, current_page: 1 }));
    }, [searchTerm, filterCountry]);

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
    };

    const handleAddClick = () => {
        setEditingHoliday(null);
        setFormData({
            name: '',
            holiday_date: '',
            country_id: isCoreAdmin ? '' : (user?.country_id || ''),
            premises_id: 0,
            status: 1
        });
        setFormErrors({});
        setShowModal(true);
    };

    const handleEditClick = (holiday) => {
        setEditingHoliday(holiday);
        setFormData({
            name: holiday.name || '',
            holiday_date: holiday.holiday_date || '',
            country_id: holiday.country_id || '',
            premises_id: holiday.premises_id ?? 0,
            status: holiday.status ?? 1
        });
        setFormErrors({});
        setShowModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) errors.name = 'Holiday name is required';
        if (!formData.holiday_date) errors.holiday_date = 'Holiday date is required';

        // Only core_admin needs to select country (client_admin uses org country automatically)
        if (isCoreAdmin && !formData.country_id) {
            errors.country_id = 'Country is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setSaveLoading(true);
            if (editingHoliday) {
                await settingsService.updatePublicHoliday(editingHoliday.id, formData);
                showToast('Holiday updated successfully', 'success');
            } else {
                await settingsService.createPublicHoliday(formData);
                showToast('Holiday created successfully', 'success');
            }
            setShowModal(false);
            loadHolidays();
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setSaveLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this holiday?')) return;

        try {
            await settingsService.deletePublicHoliday(id);
            showToast('Holiday deleted successfully', 'success');
            loadHolidays();
        } catch (error) {
            showToast(error.message, 'error');
        }
    };

    const getCountryName = (countryId) => {
        const targetId = Number(countryId);
        const country = countries.find(c => Number(c.id) === targetId);
        return country ? country.name : '-';
    };

    const countryOptions = countries.map((country) => ({
        id: String(country.id),
        name: country.name
    }));

    const scopeOptions = [
        { id: '0', name: 'All Premises (Country-wide)' },
        ...premisesOptions.map((p) => ({
            id: String(p.id),
            name: `${p.premises_name}${isCoreAdmin && p.organization_name ? ` (${p.organization_name})` : ''}`
        }))
    ];

    const getPremisesLabel = (holiday) => {
        if ((holiday.premises_id ?? 0) === 0) return 'All Premises';
        return holiday.premises_name || premisesOptions.find(p => p.id === holiday.premises_id)?.premises_name || `Premises #${holiday.premises_id}`;
    };

    return (
        <div>
            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ show: false, message: '', type: 'success' })}
            />

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                        Public Holiday Management
                    </h2>
                    <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '13px' }}>
                        {isCoreAdmin
                            ? 'Manage public holidays for different countries'
                            : 'Manage public holidays for your organization'}
                    </p>
                </div>
                <button
                    onClick={handleAddClick}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#007bff',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <i className="fas fa-plus"></i>
                    Add Holiday
                </button>
            </div>

            {/* Filters */}
            <div style={{ display: 'grid', gridTemplateColumns: isCoreAdmin ? '1fr 1fr' : '1fr', gap: '15px', marginBottom: '20px' }}>
                <input
                    type="text"
                    placeholder="Search holidays..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                        padding: '10px 15px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px'
                    }}
                />
                {/* Country Filter - Core Admin Only */}
                {isCoreAdmin && (
                    <SearchableSelect
                        options={countryOptions}
                        value={String(filterCountry || '')}
                        onChange={(value) => setFilterCountry(value || '')}
                        placeholder="All Countries"
                        displayKey="name"
                        valueKey="id"
                        searchPlaceholder="Search countries..."
                    />
                )}
            </div>

            {/* Table */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <LoadingSpinner />
                </div>
            ) : holidays.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    <i className="fas fa-calendar-day" style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.3 }}></i>
                    <h3>No holidays found</h3>
                    <p>Try adjusting your filters or add a new holiday</p>
                </div>
            ) : (
                <>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}>
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>ID</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Holiday Name</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Date</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Country</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Scope</th>
                                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#333', fontSize: '13px' }}>Status</th>
                                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#333', fontSize: '13px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {holidays.map((holiday) => (
                                    <tr key={holiday.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>{holiday.id}</td>
                                        <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500' }}>{holiday.name}</td>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>{holiday.holiday_date}</td>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>{getCountryName(holiday.country_id)}</td>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>{getPremisesLabel(holiday)}</td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            <span style={{
                                                backgroundColor: holiday.status == 1 ? '#28a745' : '#dc3545',
                                                color: '#fff',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                fontWeight: '600'
                                            }}>
                                                {holiday.status == 1 ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            <button
                                                onClick={() => handleEditClick(holiday)}
                                                style={{
                                                    padding: '6px 12px',
                                                    backgroundColor: '#007bff',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    marginRight: isCoreAdmin ? '8px' : '0'
                                                }}
                                            >
                                                <i className="fas fa-edit"></i> Edit
                                            </button>
                                            {isCoreAdmin && (
                                                <button
                                                    onClick={() => handleDelete(holiday.id)}
                                                    style={{
                                                        padding: '6px 12px',
                                                        backgroundColor: '#dc3545',
                                                        color: '#fff',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '13px'
                                                    }}
                                                >
                                                    <i className="fas fa-trash"></i> Delete
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div style={{ marginTop: '20px' }}>
                        <Pagination
                            currentPage={pagination.current_page}
                            totalPages={pagination.total_pages}
                            onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
                        />
                    </div>
                </>
            )}

            {/* Add/Edit Modal */}
            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={editingHoliday ? 'Edit Holiday' : 'Add Holiday'}
                size="large"
            >
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                            Holiday Name <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: formErrors.name ? '1px solid #dc3545' : '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                            placeholder="Enter holiday name"
                        />
                        {formErrors.name && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.name}</div>}
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                            Date <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                            type="date"
                            name="holiday_date"
                            value={formData.holiday_date}
                            onChange={handleInputChange}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: formErrors.holiday_date ? '1px solid #dc3545' : '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                        />
                        {formErrors.holiday_date && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.holiday_date}</div>}
                    </div>

                    {/* Country Selection - Core Admin Only */}
                    {isCoreAdmin && (
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                                Country <span style={{ color: 'red' }}>*</span>
                            </label>
                            <SearchableSelect
                                options={countryOptions}
                                value={String(formData.country_id || '')}
                                onChange={(value) => setFormData(prev => ({ ...prev, country_id: value || '' }))}
                                placeholder="Select country"
                                displayKey="name"
                                valueKey="id"
                                searchPlaceholder="Search countries..."
                                error={!!formErrors.country_id}
                            />
                            {formErrors.country_id && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.country_id}</div>}
                        </div>
                    )}

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                            Holiday Scope
                        </label>
                        <SearchableSelect
                            options={scopeOptions}
                            value={String(formData.premises_id ?? '0')}
                            onChange={(value) => setFormData(prev => ({ ...prev, premises_id: value ?? '0' }))}
                            placeholder="Select holiday scope"
                            displayKey="name"
                            valueKey="id"
                            searchPlaceholder="Search premises..."
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                name="status"
                                checked={formData.status === 1}
                                onChange={handleInputChange}
                                style={{ marginRight: '8px' }}
                            />
                            <span style={{ fontWeight: '500', fontSize: '14px' }}>Active</span>
                        </label>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
                        <button
                            type="button"
                            onClick={() => setShowModal(false)}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#6c757d',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                            disabled={saveLoading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#007bff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: saveLoading ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                opacity: saveLoading ? 0.7 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                            disabled={saveLoading}
                        >
                            {saveLoading && <LoadingSpinner size="small" />}
                            {saveLoading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default PublicHolidayManagement;
